import { useEffect, useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Bold, Italic, Underline, Link as LinkIcon, List, ListOrdered, Eraser } from "lucide-react";

type Props = {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minRows?: number;
};

/**
 * Leichtgewichtiger WYSIWYG-Editor mit Tabs "Visual" und "Code".
 * Speichert immer den rohen HTML/Text-String.
 */
export function RichTextEditor({ value, onChange, placeholder, minRows = 3 }: Props) {
  const [tab, setTab] = useState<"visual" | "code">("visual");
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastExternal = useRef<string>(value);

  // Externe Wertänderungen (z.B. "Übernehmen" aus TM) in den ContentEditable spiegeln,
  // ohne den Cursor bei jeder Eingabe zurückzusetzen.
  useEffect(() => {
    if (tab !== "visual") return;
    if (!editorRef.current) return;
    if (value === lastExternal.current) return;
    lastExternal.current = value;
    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value ?? "";
    }
  }, [value, tab]);

  // Beim Wechsel zu Visual den aktuellen Wert einsetzen
  useEffect(() => {
    if (tab === "visual" && editorRef.current) {
      if (editorRef.current.innerHTML !== (value ?? "")) {
        editorRef.current.innerHTML = value ?? "";
      }
      lastExternal.current = value;
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const exec = (cmd: string, arg?: string) => {
    editorRef.current?.focus();
    // execCommand ist deprecated, in allen Browsern aber weiterhin funktional und ausreichend für einfache Inline-Formatierung.
    document.execCommand(cmd, false, arg);
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      lastExternal.current = html;
      onChange(html);
    }
  };

  const insertLink = () => {
    const url = window.prompt("URL eingeben (z.B. https://example.com):");
    if (!url) return;
    exec("createLink", url);
  };

  const clearFormat = () => exec("removeFormat");

  const rows = Math.min(16, Math.max(minRows, Math.ceil((value?.length ?? 0) / 60)));

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as "visual" | "code")} className="w-full">
      <TabsList className="h-8">
        <TabsTrigger value="visual" className="text-xs h-6">Visual</TabsTrigger>
        <TabsTrigger value="code" className="text-xs h-6">Code</TabsTrigger>
      </TabsList>

      <TabsContent value="visual" className="mt-2">
        <div className="rounded-md border bg-background">
          <div className="flex items-center gap-1 border-b px-1 py-1 flex-wrap">
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onMouseDown={(e) => { e.preventDefault(); exec("bold"); }} title="Fett">
              <Bold className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onMouseDown={(e) => { e.preventDefault(); exec("italic"); }} title="Kursiv">
              <Italic className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onMouseDown={(e) => { e.preventDefault(); exec("underline"); }} title="Unterstrichen">
              <Underline className="h-3.5 w-3.5" />
            </Button>
            <span className="mx-1 h-4 w-px bg-border" />
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }} title="Liste">
              <List className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onMouseDown={(e) => { e.preventDefault(); exec("insertOrderedList"); }} title="Nummerierte Liste">
              <ListOrdered className="h-3.5 w-3.5" />
            </Button>
            <span className="mx-1 h-4 w-px bg-border" />
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onMouseDown={(e) => { e.preventDefault(); insertLink(); }} title="Link einfügen">
              <LinkIcon className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onMouseDown={(e) => { e.preventDefault(); clearFormat(); }} title="Formatierung entfernen">
              <Eraser className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            data-placeholder={placeholder}
            onInput={(e) => {
              const html = (e.target as HTMLDivElement).innerHTML;
              lastExternal.current = html;
              onChange(html);
            }}
            className="prose prose-sm max-w-none px-3 py-2 min-h-[80px] focus:outline-none text-sm [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_li]:pl-1 [&[data-placeholder]:empty:before]:content-[attr(data-placeholder)] [&[data-placeholder]:empty:before]:text-muted-foreground"
          />
        </div>
      </TabsContent>

      <TabsContent value="code" className="mt-2">
        <Textarea
          value={value}
          onChange={(e) => {
            lastExternal.current = e.target.value;
            onChange(e.target.value);
          }}
          rows={rows}
          placeholder={placeholder}
          className="font-mono text-xs"
        />
      </TabsContent>
    </Tabs>
  );
}
